import { createAwsTranscriptionService, createWhisperTranscriptionService } from '@scale/mm-shared';
import { Icon, Typography } from '@scale/scaleui';
import { sleep } from '@scale/shared/utils';
import _ from 'lodash';
import { memo, useContext } from 'react';
import { useQuery } from 'react-query';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ExpandablePre } from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/SharedComponents/ExpandablePre';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { processAudioAttachment, transcribeAudio } from 'react-scale/lib/chat-api/multimodal';
import { WithCopy } from 'react-scale/lib/sensor-fusion';

type Props = {
  audioUrl: string;
  userGeneratedAudio: boolean;
};

export const ChatAudioTranscript = memo(({ audioUrl, userGeneratedAudio }: Props) => {
  const { assignmentId, projectId } = useContext(ChatTaskContext);
  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId },
    false,
  );

  const transcriptionService = shouldUseWhisperTranscription
    ? createWhisperTranscriptionService()
    : createAwsTranscriptionService();

  const { data, isLoading, isError } = useQuery(
    ['transcribe_audio', audioUrl],
    async (): Promise<string> => {
      // fails with 409 until the audio is processed
      try {
        const data = await transcribeAudio({ audioUrl });
        return data.transcript;
      } catch (e) {
        // 404 means we haven't run processing, so try starting that
        if (_.get(e, 'status') === 404) {
          await sleep(500);
          await processAudioAttachment({
            audioUrl,
            assignmentId,
            userGeneratedAudio,
            transcriptionService,
          });
        }
        throw e;
      }
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // for 404 we only retry once, for 409 (transcript pending) we retry a long time, for all other errors just fail
      retry: (failureCount, err) =>
        (_.get(err, 'status') === 409 && failureCount < 60) ||
        (_.get(err, 'status') === 404 && failureCount < 3),
      retryDelay: attemptIndex => Math.min(30_000, Math.pow(1.1, attemptIndex) * 30_000),
    },
  );

  if (isLoading) {
    return (
      <div>
        <Typography.Caption>Loading the transcript for this audio clip...</Typography.Caption>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex gap-1 items-center">
        <Icon icon={['fas', 'times-circle']} />
        <Typography.Caption>Could not load the transcript for this audio clip</Typography.Caption>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <WithCopy copyText={data ?? ''}>
        <Typography.Caption className="mr-1">Audio transcript</Typography.Caption>
      </WithCopy>
      <ExpandablePre lineClampValue={8}>{data}</ExpandablePre>
    </div>
  );
});
